/**
 * TheoremsPanel — canonical theorems and lemmas of ILG.
 *
 * Renders T1..T3 and L1..L5 from `data/theorems.json` with claim,
 * inputs, checkable predicate, and the rulebook codes that witness or
 * refuse each statement. The list is the source of truth used by the
 * test suite to verify cross-data consistency with the rulebook.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import theoremsData from "../data/theorems.json" with { type: "json" };

const ACCENT = "#a78bfa";

interface Item {
  readonly id: string;
  readonly kind: "Theorem" | "Lemma";
  readonly label: string;
  readonly claim: string;
  readonly inputs: readonly string[];
  readonly predicate: string;
  readonly rulebookCodes: readonly string[];
}

interface BadgeRailItem {
  readonly id: string;
  readonly label: string;
  readonly color: string;
  readonly title?: string;
}

interface ItemView extends Item {
  readonly inputBadges: readonly BadgeRailItem[];
  readonly rulebookBadges: readonly BadgeRailItem[];
}

const ITEMS: readonly Item[] = theoremsData.items as readonly Item[];
const ITEM_VIEWS: readonly ItemView[] = ITEMS.map((item) => ({
  ...item,
  inputBadges: item.inputs.map((input) => ({
    id: `${item.id}-input-${input}`,
    label: input,
    color: panelTokens.muted,
  })),
  rulebookBadges: item.rulebookCodes.map((code) => ({
    id: `${item.id}-rule-${code}`,
    label: code,
    color: panelTokens.accentAmber,
  })),
}));
const THEOREMS = ITEM_VIEWS.filter((item) => item.kind === "Theorem");
const LEMMAS = ITEM_VIEWS.filter((item) => item.kind === "Lemma");

export function TheoremsPanel(): React.JSX.Element {
  return (
    <PanelSurface.PanelShell
      title="Theorems & Lemmas"
      subtitle="T1..T3 · L1..L5 — each binds a claim to a checkable predicate"
      accentColor={ACCENT}
    >
      <PanelSurface.Card>
        <p className="m-0 text-[14.5px] leading-[1.7] text-[var(--ilg-text)]">
          Each theorem and lemma is a <strong>rule family</strong>: a claim
          about lifecycle governance, the inputs the validator consults, a
          checkable predicate, and the stable rulebook codes that fire when the
          predicate fails. The triad <em>claim · predicate · code</em> is what
          makes ILG mechanizable rather than aspirational.
        </p>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>Theorems</PanelSurface.SectionHeading>
      <div
        className="grid gap-3"
        style={{
          gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))",
        }}
      >
        {THEOREMS.map((item) => (
          <ItemCard key={item.id} item={item} accent={ACCENT} />
        ))}
      </div>

      <PanelSurface.SectionHeading style={{ marginTop: 16 }}>Lemmas</PanelSurface.SectionHeading>
      <div
        className="grid gap-3"
        style={{
          gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))",
        }}
      >
        {LEMMAS.map((item) => (
          <ItemCard key={item.id} item={item} accent={panelTokens.accentTeal} />
        ))}
      </div>
    </PanelSurface.PanelShell>
  );
}

function ItemCard({
  item,
  accent,
}: {
  readonly item: ItemView;
  readonly accent: string;
}): React.JSX.Element {
  return (
    <PanelSurface.Card style={{ borderColor: `${accent}44` }}>
      <div className="flex items-baseline gap-2.5">
        <span
          className="font-mono text-[18px] font-extrabold"
          style={{
            color: accent,
            textShadow: `0 0 12px ${accent}55`,
          }}
        >
          {item.id}
        </span>
        <span className="text-[15px] font-bold text-[var(--ilg-bright)]">
          {item.label}
        </span>
      </div>

      <PanelSurface.SectionHeading style={{ marginTop: 14 }}>Claim</PanelSurface.SectionHeading>
      <p className="mt-2 text-[13.5px] leading-[1.7] text-[var(--ilg-text)]">
        {item.claim}
      </p>

      <PanelSurface.SectionHeading style={{ marginTop: 12 }}>Inputs</PanelSurface.SectionHeading>
      <PanelSurface.BadgeRail
        ariaLabel={`${item.id} theorem inputs`}
        items={item.inputBadges}
        className="mt-2"
      />

      <PanelSurface.SectionHeading style={{ marginTop: 12 }}>Predicate</PanelSurface.SectionHeading>
      <PanelSurface.Mono>{item.predicate}</PanelSurface.Mono>

      <PanelSurface.SectionHeading style={{ marginTop: 12 }}>Rulebook codes</PanelSurface.SectionHeading>
      <PanelSurface.BadgeRail
        ariaLabel={`${item.id} rulebook codes`}
        items={item.rulebookBadges}
        className="mt-2"
      />
    </PanelSurface.Card>
  );
}
